/**
 * CRM Inbox — tickets currently assigned to the signed-in user.
 *
 * Polls every 30 s through `useCrmInbox`. Acknowledging marks each
 * assignment as seen so a manager can clear new escalations off their
 * plate without opening every ticket.
 *
 * Rows say who assigned the ticket and carry the assignment note, because
 * the note is the whole reason an escalation was sent. Pending vs
 * acknowledged rows use distinct left accents and badge tones so the mixed
 * "Show acknowledged" list stays scannable.
 */
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, Inbox, UserCheck } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/Card';
import { EmptyState } from '@/components/ui/EmptyState';
import { ErrorState } from '@/components/ui/ErrorState';
import { Skeleton } from '@/components/ui/Skeleton';
import { useAuth } from '@/auth/AuthProvider';
import {
  useAcknowledgeInbox,
  useCrmInbox,
  type CrmAssignment,
  type CrmTicket,
} from '@/api/bi/crm';
import {
  TICKET_PRIORITY_LABEL,
  TICKET_PRIORITY_TONE,
  TICKET_STATUS_LABEL,
  TICKET_STATUS_TONE,
  TICKET_TYPE_LABEL,
} from '@/lib/crmStatus';
import { apiErrorMessage } from '@/lib/apiError';
import { cn } from '@/lib/cn';
import { fmt } from '@/lib/format';
import { notify } from '@/lib/toast';

const PRIORITY_RANK: Record<string, number> = {
  critical: 0,
  high: 1,
  normal: 2,
  low: 3,
};

/** The assignment row that belongs to the signed-in user, when present. */
function myAssignment(ticket: CrmTicket, userId: number | null): CrmAssignment | null {
  if (userId == null) return null;
  return (ticket.assignments ?? []).find((a) => a.user?.id === userId) ?? null;
}

export function CrmInboxPage() {
  const { user } = useAuth();
  const [includeAcknowledged, setIncludeAcknowledged] = useState(false);
  const inbox = useCrmInbox(!includeAcknowledged);
  const ack = useAcknowledgeInbox();
  const [selected, setSelected] = useState<Set<number>>(new Set());

  const tickets = useMemo(() => inbox.data?.results ?? [], [inbox.data]);
  const userId = user?.id ?? null;

  const rows = useMemo(() => {
    return [...tickets]
      .map((ticket) => {
        const assignment = myAssignment(ticket, userId);
        return {
          ticket,
          assignment,
          acknowledgedAt: assignment?.acknowledged_at ?? null,
          // Prefer the assignment timestamp. When the API is filtered to
          // unacked-only, every row is pending; when mixed, this is what
          // drives Pending vs Acknowledged chrome.
          isNew: !assignment?.acknowledged_at,
        };
      })
      .sort((a, b) => {
        if (a.isNew !== b.isNew) return a.isNew ? -1 : 1;
        const diff =
          (PRIORITY_RANK[a.ticket.priority] ?? 4) - (PRIORITY_RANK[b.ticket.priority] ?? 4);
        if (diff !== 0) return diff;
        return (
          new Date(b.ticket.created_at).getTime() - new Date(a.ticket.created_at).getTime()
        );
      });
  }, [tickets, userId, includeAcknowledged]);

  const newRows = rows.filter((r) => r.isNew);
  const acknowledgedCount = rows.length - newRows.length;
  const selectableIds = newRows.map((r) => r.ticket.id);
  const allSelected =
    selectableIds.length > 0 && selectableIds.every((id) => selected.has(id));

  function toggle(id: number) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleAll() {
    setSelected(allSelected ? new Set() : new Set(selectableIds));
  }

  async function handleAcknowledge() {
    if (selected.size === 0) return;
    const count = selected.size;
    try {
      await ack.mutateAsync(Array.from(selected));
      notify.success(`${count} ticket${count === 1 ? '' : 's'} acknowledged.`);
      setSelected(new Set());
    } catch (err) {
      notify.error(apiErrorMessage(err, 'Could not acknowledge those tickets.'));
    }
  }

  return (
    <>
      <PageHeader
        eyebrow="CRM"
        title="My inbox"
        description="Tickets assigned to you. Acknowledging tells the assigner you have seen it."
        actions={
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setIncludeAcknowledged((v) => !v);
                setSelected(new Set());
              }}
            >
              {includeAcknowledged ? 'Pending only' : 'Show acknowledged'}
            </Button>
            <Button
              size="sm"
              onClick={handleAcknowledge}
              disabled={selected.size === 0 || ack.isPending}
              className="gap-1"
            >
              <CheckCircle2 className="h-3.5 w-3.5" />
              {ack.isPending
                ? 'Acknowledging…'
                : selected.size > 0
                  ? `Acknowledge ${selected.size}`
                  : 'Acknowledge'}
            </Button>
          </div>
        }
      />

      <Card>
        <CardHeader>
          <CardTitle className="inline-flex items-center gap-2">
            <Inbox className="h-4 w-4" /> {newRows.length} pending
            {includeAcknowledged ? ` · ${acknowledgedCount} acknowledged` : ''}
          </CardTitle>
          {selectableIds.length > 0 ? (
            <label className="inline-flex cursor-pointer select-none items-center gap-2 text-xs text-muted-foreground">
              <input
                type="checkbox"
                checked={allSelected}
                onChange={toggleAll}
                className="h-3.5 w-3.5 accent-primary"
              />
              Select all pending
            </label>
          ) : null}
        </CardHeader>
        <CardBody className="p-0">
          {inbox.isError ? (
            <div className="px-5 py-6">
              <ErrorState onRetry={() => void inbox.refetch()} />
            </div>
          ) : inbox.isLoading ? (
            <div className="space-y-2 px-5 py-4">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : rows.length === 0 ? (
            <EmptyState
              icon={<Inbox className="h-5 w-5" />}
              title="Inbox zero"
              description={
                includeAcknowledged
                  ? 'No tickets are assigned to you right now.'
                  : 'Nothing pending. Switch to "Show acknowledged" for the ones you have already seen.'
              }
            />
          ) : (
            <ul className="divide-y divide-border">
              {rows.map(({ ticket, assignment, acknowledgedAt, isNew }) => (
                <li
                  key={ticket.id}
                  className={cn(
                    'flex items-start gap-3 border-l-2 px-5 py-3 hover:bg-muted/30',
                    isNew
                      ? 'border-l-warning bg-warning/5'
                      : 'border-l-success/60 bg-transparent opacity-80',
                  )}
                >
                  <input
                    type="checkbox"
                    checked={selected.has(ticket.id)}
                    onChange={() => toggle(ticket.id)}
                    disabled={!isNew}
                    aria-label={`Select ticket ${ticket.id}`}
                    className="mt-1 h-3.5 w-3.5 accent-primary disabled:opacity-40"
                  />
                  <div className="min-w-0 flex-1 space-y-1">
                    <div className="flex flex-wrap items-center gap-2 text-sm">
                      <Link
                        to={`/crm/tickets/${ticket.id}`}
                        className="font-medium hover:underline"
                      >
                        #{ticket.id} {ticket.summary || TICKET_TYPE_LABEL[ticket.type]}
                      </Link>
                      {isNew ? (
                        <Badge tone="warning">Pending</Badge>
                      ) : (
                        <Badge tone="success" className="gap-1">
                          <UserCheck className="h-3 w-3" /> Acknowledged
                          {acknowledgedAt ? ` ${fmt.ago(acknowledgedAt)}` : ''}
                        </Badge>
                      )}
                      <Badge tone={TICKET_STATUS_TONE[ticket.status]}>
                        {TICKET_STATUS_LABEL[ticket.status]}
                      </Badge>
                      <Badge tone={TICKET_PRIORITY_TONE[ticket.priority]}>
                        {TICKET_PRIORITY_LABEL[ticket.priority]}
                      </Badge>
                      {ticket.is_auto_created ? <Badge tone="info">Auto</Badge> : null}
                    </div>
                    <div className="text-[11px] text-muted-foreground">
                      {TICKET_TYPE_LABEL[ticket.type]}
                      {ticket.store_name ? ` · ${ticket.store_name}` : ''}
                      {ticket.order_id ? ` · order #${ticket.order_id}` : ''}
                      {' · created '}
                      {fmt.ago(ticket.created_at)}
                      {assignment?.assigned_by
                        ? ` · assigned by ${assignment.assigned_by.name}`
                        : ''}
                      {assignment?.created_at ? ` ${fmt.ago(assignment.created_at)}` : ''}
                    </div>
                    {assignment?.note?.trim() ? (
                      <p className="whitespace-pre-wrap rounded-md border border-border bg-muted/30 px-2 py-1.5 text-[11px] text-foreground/90">
                        {assignment.note}
                      </p>
                    ) : null}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardBody>
      </Card>
    </>
  );
}
